import { useContext } from 'react';
import {
  DialogClose,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
} from '@qodo/design-system';
import { AuthContext } from 'app/auth';
import {
  useDeleteInviteMutation,
  useDeleteSelfMutation,
  useDeleteMemberMutation,
} from '../../network/mutations';
import { spawn } from '../../utils/translations';
import { DELETION_TARGETS } from './consts';
import { DeleteMemberDialogProps } from './types';

export const DeleteMemberDialog = (props: DeleteMemberDialogProps) => {
  const { member, onOpenChange = () => {}, target } = props;
  const { signout } = useContext(AuthContext);

  const spawnModalContent = spawn(`dialogs.delete-${target}`);
  const spawnModalActions = spawn(`dialogs.delete-${target}.action`);

  const { mutateAsync: deleteMember, isPending: isDeleteMemberPending } =
    useDeleteMemberMutation();
  const { mutateAsync: deleteSelf, isPending: isDeleteSelfPending } =
    useDeleteSelfMutation();
  const { mutateAsync: deleteInvite, isPending: isDeleteInvitePending } =
    useDeleteInviteMutation();

  const onSubmit = async () => {
    switch (target) {
      case DELETION_TARGETS.MEMBER:
        await deleteMember(member.id);
        break;
      case DELETION_TARGETS.INVITE:
        await deleteInvite(member.id);
        break;
      case DELETION_TARGETS.SELF:
        await deleteSelf();
        signout();
        break;
    }

    onOpenChange(false);
  };

  const pendingStates = {
    [DELETION_TARGETS.MEMBER]: isDeleteMemberPending,
    [DELETION_TARGETS.INVITE]: isDeleteInvitePending,
    [DELETION_TARGETS.SELF]: isDeleteSelfPending,
  } as const;

  const isPending = pendingStates[target] ?? false;

  return (
    <Dialog {...props}>
      <DialogContent autoFocus={false} className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-h3">
            {spawnModalContent('title')}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription>
          {spawnModalContent('description')}
        </DialogDescription>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={isPending}>
              {spawnModalActions('cancel')}
            </Button>
          </DialogClose>
          <Button variant="danger" onClick={onSubmit} loading={isPending}>
            {spawnModalActions('delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
